// Pure calendar math over ISO `YYYY-MM-DD` date strings, used by the mobile
// attendance week view (see `features/mobile-admin.md`). All arithmetic runs
// on UTC epoch days so results never depend on the host timezone or DST; only
// `isoDateOf` deliberately reads a `Date`'s *local* calendar fields, since
// "today" on a device is a local notion.

import { isRealIsoDate } from './registration';
import { WEEKDAYS } from './trainingClasses';
import type { Weekday } from './types';

/** Milliseconds in one day (UTC days have no DST). */
const DAY_MS = 86_400_000;

/** UTC epoch ms of an ISO date's midnight; throws on a malformed date. */
function epochOf(date: string): number {
  if (!isRealIsoDate(date)) {
    throw new RangeError(`Invalid ISO calendar date: "${date}"`);
  }
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Format a UTC epoch ms back to an ISO `YYYY-MM-DD` date. */
function isoOf(epoch: number): string {
  return new Date(epoch).toISOString().slice(0, 10);
}

/** The local calendar date of a `Date` (e.g. "today" on the device) as ISO. */
export function isoDateOf(moment: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`;
}

/** The ISO date `days` calendar days after (negative: before) `date`. */
export function addDaysIso(date: string, days: number): string {
  if (!Number.isInteger(days)) {
    throw new RangeError(`Day offset must be an integer, got ${days}`);
  }
  return isoOf(epochOf(date) + days * DAY_MS);
}

/** The weekday code (`MON`…`SUN`) an ISO date falls on. */
export function weekdayOfIso(date: string): Weekday {
  // `getUTCDay` counts Sun=0…Sat=6; WEEKDAYS is ordered Mon…Sun.
  return WEEKDAYS[(new Date(epochOf(date)).getUTCDay() + 6) % 7];
}

/** The Mon…Sun week containing `date`, as 7 consecutive ISO dates. */
export function weekOfIso(date: string): string[] {
  const monday = epochOf(date) - WEEKDAYS.indexOf(weekdayOfIso(date)) * DAY_MS;
  return WEEKDAYS.map((_, i) => isoOf(monday + i * DAY_MS));
}

/** The day-of-month (1–31) of an ISO date, for compact calendar cells. */
export function dayOfMonthIso(date: string): number {
  return new Date(epochOf(date)).getUTCDate();
}
